<template>
    <b-row>
        <b-col>
            <card class="mb-2">
                <b-row>
                    <b-col cols="12" md="6" lg="5">
                        <SearchComponent ref="dashboardSearcher" v-model="selectedDashboard" label="Select Dashboard"
                            search-u-r-l="/AdhocDashboard" createNewObject createNewLabel="Create New Dashboard..."
                            prepopulate @input="dashboardSelected" @createNewObject="createNewDashboard"
                            :key="selectedDashboard.dashboardId" />
                    </b-col>
                    <b-col v-if="selectedDashboard.dashboardId" cols="12" md="6" lg="5">
                        <label>Share With Teams:</label>
                        <multiselect v-model="activeSharedWith" :options="assignToOptions" :multiple="true"
                            group-values="options" group-label="type" label="name" track-by="id"
                            :disabled="!(canViewAllDashboards || selectedDashboardIsMine)">
                            <template #option="data">
                                <i v-if="data.option.$groupLabel === 'Teams'" class="fa fa-users"></i>
                                {{ data.option.$groupLabel }}
                                {{ data.option.name }}
                            </template>
                            <template #tag="data">
                                <b-badge variant="primary" class="mr-1 my-0">
                                    <i class="fa fa-users"></i>
                                    {{ data.option.name }}
                                    <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                </b-badge>
                            </template>
                        </multiselect>
                    </b-col>
                </b-row>
            </card>
        </b-col>
        <b-col cols="12">
            <div id="revealView"></div>
        </b-col>
    </b-row>
</template>

<script lang='ts'>
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import SearchComponent from "@/components/SearchComponent.vue";
import { AdhocDash } from "@/models/AdhocDashboard";
import Axios from 'axios';
import Multiselect from 'vue-multiselect';
import { Team } from '@/models/Team';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

declare let $: any;

@Component({
    components: {
        SearchComponent,
        Multiselect
    }
})
export default class AdhocDashboard extends Vue {
    private selectedDashboard: AdhocDash = new AdhocDash;
    private revealView: any;
    private startDate: Date = new Date();
    private endDate: Date = new Date();
    private isNewDashboard: boolean = false;

    private activeSharedWith: any = [];
    protected teams: Team[] = [];
    protected loadingSharedWith = false;

    @Prop({ default: null }) protected dashboardId?: string | null;

    created() {
        this.startDate.setDate(1);
        //this.getTeams();
    }

    async mounted() {
        await this.$loadScript("https://dl.revealbi.io/reveal/libs/1.7.5/infragistics.reveal.js");
        const serverURL = this?.$config?.VITE_APP_APIURL ?? '';

        $.ig.RVUtility.prototype.maxCellsRestriction = 10_000_000;
        $.ig.RevealSdkSettings.setBaseUrl(serverURL);
        $.ig.RevealSdkSettings.setAdditionalHeadersProvider((url: any) => {
            const headers: Record<string, any> = {};
            headers["Authorization"] = Axios.defaults.headers?.Authorization ?? "";
            headers["StartDate"] = this.startDate.getTime();
            headers["EndDate"] = this.endDate.getTime();
            headers["IsNewDashboard"] = this.isNewDashboard.toString();
            return headers;
        });
        this.revealView = new $.ig.RevealView("#revealView");

        this.revealView.canSaveAs = false;
        this.revealView.showEditDataSource = true;
        this.revealView.onSave = async (_: any, args: any) => {
            let wasNewDashboard = this.isNewDashboard;

            // If this is a new dashboard, compute the final dashboard ID (with GUID if collision)
            if (this.isNewDashboard) {
                try {
                    const response = await this.$http.post("/AdhocDashboard/compute-id", {
                        dashboardId: args.name,
                        isNew: true
                    });

                    const finalDashboardId = response.data.finalDashboardId;
                    args.dashboardId = finalDashboardId;
                    args.name = finalDashboardId;

                    this.isNewDashboard = false;
                } catch (err) {
                    console.error("Failed to compute dashboard ID:", err);
                    this.$notification(NotificationOptions.error("Failed to compute dashboard ID"));
                    return;
                }
            }

            args.saveFinished();
            this.selectedDashboard.title = args.name;

            // After first save of a new dashboard, fetch full details and initialize UI
            if (wasNewDashboard) {
                this.selectedDashboard.dashboardId = args.dashboardId;

                // Wait a moment for the save to complete on the backend
                await new Promise(resolve => setTimeout(resolve, 500));

                try {
                    const resp = await this.$http.get("/AdhocDashboard", { params: { searchTerm: args.dashboardId } });
                    if (resp.data?.length) {
                        this.selectedDashboard = Object.assign(new AdhocDash(), resp.data[0]);

                        this.$router.replace({ name: "AdhocDashboard", params: { dashboardId: this.selectedDashboard.dashboardId } });

                        const dashboardSearcher = this.$refs.dashboardSearcher as any;
                        if (dashboardSearcher?.reloadResults) {
                            dashboardSearcher.reloadResults();
                        }

                        this.loadingSharedWith = true;
                        this.activeSharedWith = (this.selectedDashboard as any).sharedWithTeams || [];
                        this.$nextTick(() => {
                            this.loadingSharedWith = false;
                        });
                    }
                } catch (err) {
                    console.error("Failed to fetch saved dashboard:", err);
                }
            }

            this.$notification(NotificationOptions.successSaveNotificationPreset("Dashboard"));
        };
        this.revealView.onDataSourcesRequested = this.dataSourcesRequested;

        await this.getTeams();
        await this.loadLinkedReport();
    }

    async loadLinkedReport() {
        if (!this.dashboardId) return;

        try {
            const resp = await this.$http.get("/AdhocDashboard", { params: { searchTerm: this.dashboardId } });
            if (!resp.data?.length) return;
            this.selectedDashboard = Object.assign(new AdhocDash(), resp.data[0]);
            this.dashboardSelected();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    createNewDashboard() {
        // User selected "Create New Dashboard..." - set flag and create empty dashboard
        this.isNewDashboard = true;
        this.selectedDashboard = new AdhocDash();
        this.revealView.dashboard = new $.ig.RVDashboard();
        this.activeSharedWith = [];
    }

    dashboardSelected() {
        // Loading an existing dashboard - set flag to false
        this.isNewDashboard = false;

        this.$router.replace({ name: "AdhocDashboard", params: { dashboardId: this.selectedDashboard.dashboardId } });

        $.ig.RVDashboard.loadDashboard(this.selectedDashboard.dashboardId, (dashboard: any) => {
            //console.log(dashboard);
            this.revealView.dashboard = dashboard ?? new $.ig.RVDashboard();
            this.revealView.onDataSourcesRequested = this.dataSourcesRequested;
        });

        this.loadingSharedWith = true;
        this.activeSharedWith = (this.selectedDashboard as any).sharedWithTeams;
        this.$nextTick(() => {
            this.loadingSharedWith = false;
        })
    }

    dataSourcesRequested(callback: Function) {
        const sqlDataSource = new $.ig.RVSqlServerDataSource();
        sqlDataSource.id = "MySqlServerDatasource";
        sqlDataSource.title = "Scriptly";
        callback(new $.ig.RevealDataSources([sqlDataSource], [], false));
    }

    // startDateUpdated(val: any) {
    //     this.revealView.refreshDashboardData();
    // }

    // endDateUpdated(val: any) {
    //     this.revealView.refreshDashbaordData();
    // }

    async getTeams() {
        try {
            const response = await this.$http.get<Team[]>("/user/teams");
            this.teams = response.data;
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    get assignToOptions() {
        return [
            {
                type: 'Teams',
                options: this.teams.map(t => ({
                    id: t.id,
                    name: t.name,
                    isTeam: true
                }))
            }
        ]
    }

    @Watch("activeSharedWith")
    handleActiveSharedWithChange() {
        if (this.loadingSharedWith) return;
        this.updateSharedTeams();
    }

    async updateSharedTeams() {
        try {
            await this.$http.post(`/AdhocDashboard/teams/${this.selectedDashboard.dashboardId}`, this.activeSharedWith);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get canViewAllDashboards() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Analytics/View All Dashboards".toLowerCase());
    }

    get selectedDashboardIsMine() {
        return this.selectedDashboard?.createdBy?.toLowerCase() === this.$user?.userName?.toLowerCase();
    }
};
</script>

<style scoped>
#revealView {
    height: calc(100vh - 230px);
    width: 100%;
    position: relative;
}

::v-deep(#searcher .vs__dropdown-toggle) {
    height: 43px;
}
</style>
